"use client";

import React from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Label } from "@/app/components/ui/label";
import { Input } from "@/app/components/ui/input";
import { Button } from "@/app/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/app/components/ui/accordion";
import { Slider } from "@/app/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Textarea } from "@/app/components/ui/textarea";
import { Switch } from "@/app/components/ui/switch";
import { 
  XCircle, 
  Settings, 
  Link, 
  Smartphone, 
  Wifi, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Bitcoin, 
  CreditCard,
  Globe,
  MessageSquare,
  Video,
  Mic,
  Hash
} from "lucide-react";
import { QRState } from "./types";

interface QRFormProps {
  state: QRState;
  updateState: (update: Partial<QRState>) => void;
  onGenerate: () => void;
  onReset: () => void;
  isGenerating: boolean;
  isGettingLocation: boolean;
  onGetLocation: () => void;
}

export default function QRForm({
  state,
  updateState,
  onGenerate,
  onReset,
  isGenerating,
  isGettingLocation,
  onGetLocation,
}: QRFormProps) {
  return (
    <Card className="card-enhanced">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          QR Code Generator
        </CardTitle>
        <CardDescription>
          Create QR codes for various content types
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Content Type Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Content Type</Label>
          <Select value={state.kind} onValueChange={(value) => updateState({ kind: value as any, content: {} })}>
            <SelectTrigger>
              <SelectValue placeholder="Select content type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="url">
                <div className="flex items-center gap-2">
                  <Link className="h-4 w-4" />
                  URL
                </div>
              </SelectItem>
              <SelectItem value="text">
                <div className="flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  Text
                </div>
              </SelectItem>
              <SelectItem value="wifi">
                <div className="flex items-center gap-2">
                  <Wifi className="h-4 w-4" />
                  WiFi
                </div>
              </SelectItem>
              <SelectItem value="vcard">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Contact (vCard)
                </div>
              </SelectItem>
              <SelectItem value="mailto">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </div>
              </SelectItem>
              <SelectItem value="tel">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone
                </div>
              </SelectItem>
              <SelectItem value="sms">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  SMS
                </div>
              </SelectItem>
              <SelectItem value="geo">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Location
                </div>
              </SelectItem>
              <SelectItem value="calendar">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Calendar Event
                </div>
              </SelectItem>
              <SelectItem value="bitcoin">
                <div className="flex items-center gap-2">
                  <Bitcoin className="h-4 w-4" />
                  Bitcoin
                </div>
              </SelectItem>
              <SelectItem value="epc">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  EPC (SEPA)
                </div>
              </SelectItem>
              <SelectItem value="gs1-digital-link">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  GS1 Digital Link
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Content Input Fields */}
        <div className="space-y-4">
          {state.kind === 'url' && (
            <div className="space-y-2">
              <Label htmlFor="url">Website URL</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com"
                value={state.content.url || ''}
                onChange={(e) => updateState({ content: { ...state.content, url: e.target.value } })}
              />
            </div>
          )}

          {state.kind === 'text' && (
            <div className="space-y-2">
              <Label htmlFor="text">Text Content</Label>
              <Textarea
                id="text"
                placeholder="Enter your text here..."
                value={state.content.text || ''}
                onChange={(e) => updateState({ content: { ...state.content, text: e.target.value } })}
                rows={3}
              />
            </div>
          )}

          {state.kind === 'wifi' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ssid">Network Name (SSID)</Label>
                <Input
                  id="ssid"
                  placeholder="MyWiFiNetwork"
                  value={state.content.ssid || ''}
                  onChange={(e) => updateState({ content: { ...state.content, ssid: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="WiFi password"
                  value={state.content.password || ''}
                  onChange={(e) => updateState({ content: { ...state.content, password: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="auth">Security Type</Label>
                <Select value={state.content.auth || 'WPA'} onValueChange={(value) => updateState({ content: { ...state.content, auth: value } })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WPA">WPA/WPA2</SelectItem>
                    <SelectItem value="WEP">WEP</SelectItem>
                    <SelectItem value="nopass">No Password</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="hidden"
                  checked={state.content.hidden || false}
                  onCheckedChange={(checked) => updateState({ content: { ...state.content, hidden: checked } })}
                />
                <Label htmlFor="hidden">Hidden Network</Label>
              </div>
            </div>
          )}

          {state.kind === 'vcard' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={state.content.firstName || ''}
                    onChange={(e) => updateState({ content: { ...state.content, firstName: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={state.content.lastName || ''}
                    onChange={(e) => updateState({ content: { ...state.content, lastName: e.target.value } })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org">Organization</Label>
                <Input
                  id="org"
                  placeholder="Company Inc."
                  value={state.content.org || ''}
                  onChange={(e) => updateState({ content: { ...state.content, org: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Software Engineer"
                  value={state.content.title || ''}
                  onChange={(e) => updateState({ content: { ...state.content, title: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tel">Phone</Label>
                <Input
                  id="tel"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={state.content.tel || ''}
                  onChange={(e) => updateState({ content: { ...state.content, tel: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={state.content.email || ''}
                  onChange={(e) => updateState({ content: { ...state.content, email: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="url">Website</Label>
                <Input
                  id="url"
                  type="url"
                  placeholder="https://johndoe.com"
                  value={state.content.url || ''}
                  onChange={(e) => updateState({ content: { ...state.content, url: e.target.value } })}
                />
              </div>
            </div>
          )}

          {state.kind === 'mailto' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="to">Recipient Email</Label>
                <Input
                  id="to"
                  type="email"
                  placeholder="recipient@example.com"
                  value={state.content.to || ''}
                  onChange={(e) => updateState({ content: { ...state.content, to: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="Email subject"
                  value={state.content.subject || ''}
                  onChange={(e) => updateState({ content: { ...state.content, subject: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="body">Message Body</Label>
                <Textarea
                  id="body"
                  placeholder="Email message..."
                  value={state.content.body || ''}
                  onChange={(e) => updateState({ content: { ...state.content, body: e.target.value } })}
                  rows={3}
                />
              </div>
            </div>
          )}

          {state.kind === 'tel' && (
            <div className="space-y-2">
              <Label htmlFor="to">Phone Number</Label>
              <Input
                id="to"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={state.content.to || ''}
                onChange={(e) => updateState({ content: { ...state.content, to: e.target.value } })}
              />
            </div>
          )}

          {state.kind === 'sms' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="to">Phone Number</Label>
                <Input
                  id="to"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={state.content.to || ''}
                  onChange={(e) => updateState({ content: { ...state.content, to: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="body">Message</Label>
                <Textarea
                  id="body"
                  placeholder="SMS message..."
                  value={state.content.body || ''}
                  onChange={(e) => updateState({ content: { ...state.content, body: e.target.value } })}
                  rows={3}
                />
              </div>
            </div>
          )}

          {state.kind === 'geo' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lat">Latitude</Label>
                  <Input
                    id="lat"
                    type="number"
                    step="any"
                    placeholder="40.7128"
                    value={state.content.lat || ''}
                    onChange={(e) => updateState({ content: { ...state.content, lat: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lng">Longitude</Label>
                  <Input
                    id="lng"
                    type="number"
                    step="any"
                    placeholder="-74.0060"
                    value={state.content.lng || ''}
                    onChange={(e) => updateState({ content: { ...state.content, lng: e.target.value } })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="query">Location Name (Optional)</Label>
                <Input
                  id="query"
                  placeholder="New York, NY"
                  value={state.content.query || ''}
                  onChange={(e) => updateState({ content: { ...state.content, query: e.target.value } })}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={onGetLocation}
                disabled={isGettingLocation}
                className="w-full"
              >
                {isGettingLocation ? 'Getting Location...' : 'Use Current Location'}
              </Button>
            </div>
          )}

          {state.kind === 'calendar' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="summary">Event Title</Label>
                <Input
                  id="summary"
                  placeholder="Meeting with team"
                  value={state.content.summary || ''}
                  onChange={(e) => updateState({ content: { ...state.content, summary: e.target.value } })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start">Start Date/Time</Label>
                  <Input
                    id="start"
                    type="datetime-local"
                    value={state.content.start || ''}
                    onChange={(e) => updateState({ content: { ...state.content, start: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end">End Date/Time</Label>
                  <Input
                    id="end"
                    type="datetime-local"
                    value={state.content.end || ''}
                    onChange={(e) => updateState({ content: { ...state.content, end: e.target.value } })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="Conference Room A"
                  value={state.content.location || ''}
                  onChange={(e) => updateState({ content: { ...state.content, location: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Event description..."
                  value={state.content.description || ''}
                  onChange={(e) => updateState({ content: { ...state.content, description: e.target.value } })}
                  rows={3}
                />
              </div>
            </div>
          )}

          {state.kind === 'bitcoin' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Bitcoin Address</Label>
                <Input
                  id="address"
                  placeholder="1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
                  value={state.content.address || ''}
                  onChange={(e) => updateState({ content: { ...state.content, address: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (BTC)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="any"
                  placeholder="0.001"
                  value={state.content.amount || ''}
                  onChange={(e) => updateState({ content: { ...state.content, amount: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Input
                  id="message"
                  placeholder="Payment for services"
                  value={state.content.message || ''}
                  onChange={(e) => updateState({ content: { ...state.content, message: e.target.value } })}
                />
              </div>
            </div>
          )}

          {state.kind === 'epc' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Recipient Name</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={state.content.name || ''}
                  onChange={(e) => updateState({ content: { ...state.content, name: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  placeholder="DE89370400440532013000"
                  value={state.content.iban || ''}
                  onChange={(e) => updateState({ content: { ...state.content, iban: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (EUR)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="any"
                  placeholder="100.00"
                  value={state.content.amount || ''}
                  onChange={(e) => updateState({ content: { ...state.content, amount: e.target.value } })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="remittance">Remittance Information</Label>
                <Input
                  id="remittance"
                  placeholder="Invoice #12345"
                  value={state.content.remittance || ''}
                  onChange={(e) => updateState({ content: { ...state.content, remittance: e.target.value } })}
                />
              </div>
            </div>
          )}

          {state.kind === 'gs1-digital-link' && (
            <div className="space-y-2">
              <Label htmlFor="uri">GS1 Digital Link URI</Label>
              <Input
                id="uri"
                type="url"
                placeholder="https://id.gs1.org/01/01234567890123"
                value={state.content.uri || ''}
                onChange={(e) => updateState({ content: { ...state.content, uri: e.target.value } })}
              />
            </div>
          )}
        </div>

        {/* Advanced Options */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="advanced">
            <AccordionTrigger>Advanced Options</AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Error Correction Level</Label>
                  <Select value={state.ecl} onValueChange={(value) => updateState({ ecl: value as any })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">L (Low ~7%)</SelectItem>
                      <SelectItem value="M">M (Medium ~15%)</SelectItem>
                      <SelectItem value="Q">Q (Quartile ~25%)</SelectItem>
                      <SelectItem value="H">H (High ~30%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Size: {state.width}px</Label>
                  <Slider
                    value={[state.width]}
                    onValueChange={([value]) => updateState({ width: value })}
                    min={100}
                    max={1000}
                    step={10}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Margin: {state.marginModules} modules</Label>
                  <Slider
                    value={[state.marginModules]}
                    onValueChange={([value]) => updateState({ marginModules: value })}
                    min={0}
                    max={10}
                    step={1}
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="colorDark">Dark Color</Label>
                    <Input
                      id="colorDark"
                      type="color"
                      value={state.colorDark}
                      onChange={(e) => updateState({ colorDark: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="colorLight">Light Color</Label>
                    <Input
                      id="colorLight"
                      type="color"
                      value={state.colorLight}
                      onChange={(e) => updateState({ colorLight: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="svg"
                    checked={state.svg}
                    onCheckedChange={(checked) => updateState({ svg: checked })}
                  />
                  <Label htmlFor="svg">Generate as SVG</Label>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
      
      <CardFooter className="flex flex-col gap-3">
        <Button
          onClick={onGenerate}
          disabled={isGenerating}
          className="w-full btn-enhanced hover-lift"
        >
          {isGenerating ? 'Generating...' : 'Generate QR Code'}
        </Button>
        
        <Button
          variant="outline"
          onClick={onReset}
          className="w-full"
        >
          <XCircle className="mr-2 h-4 w-4" />
          Reset Form
        </Button>
      </CardFooter>
    </Card>
  );
}
